import { StoryBoard } from 'services/storyboard.service';
import { MIN_CELL_DUR } from 'constants/params';
import { RootState } from 'store/store';
import { useAppDispatch, useAppStore } from 'store/store-hooks';
import { createSetFromArray } from 'components/app/_helpers/_app.helper';
import { handleMarkers } from 'components/app/editor/_mediator/editor.slice';
import { markersConversion, markersInitFromBE, sortMarkers } from './editor-markers.helper';
import { useMarkersCb } from './use-markers-cb';

const ERR_ID = 'use-markers-reset.ts';

export const useMarkersReset = () => {
  const { delMarkers, addMarkers } = useMarkersCb();
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const getSb = (resetedMarkers: MarkerId[], markers: MarkerId[], storyBoard: StoryBoard) => {
    const markersSet = createSetFromArray(markers);
    const newMarkersSet = createSetFromArray(resetedMarkers);
    const markersToDel: MarkerId[] = [];
    const markersToAdd: MarkerId[] = [];

    markers.forEach((markerId) => {
      if (!newMarkersSet.has(markerId)) markersToDel.push(markerId);
    });
    newMarkersSet.forEach((markerId) => {
      if (!markersSet.has(markerId)) markersToAdd.push(markerId);
    });

    const res1 = addMarkers(markersToAdd, {
      markers,
      storyBoard,
      dispatch: false,
      fixLinks: false,
      handleError: false,
    });
    const res2 =
      res1 &&
      delMarkers({
        markers: res1.markers,
        storyBoard: res1.storyBoard,
        selectedMarkers: getMarkerIndex(markersToDel, res1.markers),
        dispatch: false,
      });
    return res2?.storyBoard;
  };

  return (markerPresetId: MarkerPresetId) => {
    const { selectedMarkers, markers, storyBoard, markerPresets, selectedMarkerPreset, storyConfig } = selectData(
      store.getState()
    );
    if (!markers || !storyConfig) return;

    const { transitionTime, clipTransitionAlignment } = storyConfig;
    const preset = markerPresets.find((el) => el.id === markerPresetId);
    if (!preset) {
      console.error(`[${ERR_ID}] resetMarkers: preset '${markerPresetId}' is not existed`);
      return;
    }
    const markers_0 = markersConversion('fromBE', preset.value, transitionTime, clipTransitionAlignment);
    const markers_1 = markersInitFromBE(markers_0);
    const markers_2 = selectedMarkers.length ? getMergedMarkers(selectedMarkers, markers, markers_1) : markers_1;
    const sb = getSb(markers_2, markers, storyBoard);

    sb &&
      dispatch(
        handleMarkers({
          data: {
            markers: markers_2,
            storyBoard: sb,
            selectedMarkerPreset: markerPresetId !== selectedMarkerPreset ? markerPresetId : undefined,
          },
          backupParams: { title: 'Reset Markers' },
        })
      );
  };
};

/* H E L P E R S */

const getMergedMarkers = (selectedMarkers: MarkerId[], oldMarkers: MarkerId[], resetedMarkers: MarkerId[]) => {
  const sortedSelectedMarkers = sortMarkers([...selectedMarkers]);
  const firstSelectedMarker = sortedSelectedMarkers[0];
  const lastSelectedMarker = sortedSelectedMarkers[sortedSelectedMarkers.length - 1];
  const markersBeforeSelection = oldMarkers.slice(0, firstSelectedMarker + 1);
  const startMarkerRangeTime = oldMarkers[firstSelectedMarker];
  const endMarkerRangeTime = oldMarkers[lastSelectedMarker];
  if (sortedSelectedMarkers.length === 1) {
    const incomingMarkers = resetedMarkers.filter((time) => time > startMarkerRangeTime + MIN_CELL_DUR);
    return [...markersBeforeSelection, ...incomingMarkers];
  }
  const incomingMarkers = resetedMarkers.filter(
    (time) => time > startMarkerRangeTime + MIN_CELL_DUR && time + MIN_CELL_DUR <= endMarkerRangeTime
  );
  return [...markersBeforeSelection, ...incomingMarkers, ...oldMarkers.slice(lastSelectedMarker)];
};

const getMarkerIndex = (time: Seconds[], markers: MarkerId[]) => {
  return time.map((time) => markers.findIndex((markerId) => markerId === time));
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const { markers, selectedMarkers, storyBoard, markerPresets, selectedMarkerPreset, storyConfig } = state.editor;
  return {
    selectedMarkerPreset,
    selectedMarkers,
    markerPresets,
    storyConfig,
    storyBoard,
    markers,
  };
};
